import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Chip,
  Paper,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";

interface ViewBookingDialogProps {
  open: boolean;
  onClose: () => void;
  selectedBooking: any;
  onEditStatus: (booking: any) => void;
}

const getStatusChipColor = (status: string) => {
  switch (status) {
    case "confirmed":
      return "success";
    case "pending":
      return "warning";
    case "send_invoice":
      return "primary";
    case "pay_at_counter":
      return "warning";
    case "group_on":
      return "secondary";
    case "cancelled":
      return "error";
    case "invoice_sent":
      return "info";
    default:
      return "default";
  }
};

const ViewBookingDialog: React.FC<ViewBookingDialogProps> = ({
  open,
  onClose,
  selectedBooking,
  onEditStatus,
}) => {
  if (!selectedBooking) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Booking Details</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Customer Information
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Typography variant="body1">
                {selectedBooking.bookingInfo.userDetails.name}
              </Typography>
              <Chip
                icon={<EmailIcon />}
                label={selectedBooking.bookingInfo.userDetails.email}
                size="small"
                variant="outlined"
              />
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Typography variant="body1">Phone Number:</Typography>
              <Chip
                label={selectedBooking.bookingInfo.userDetails.phoneNumber}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Booking Information
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Lane/Cage:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {selectedBooking.bookingInfo.laneOrCage.name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Booking Status:</Typography>
                <Chip
                  label={selectedBooking.bookingInfo.statusHistoryData.bookingStatus}
                  size="small"
                  color={
                    getStatusChipColor(
                      selectedBooking.bookingInfo.statusHistoryData.bookingStatus
                    ) as any
                  }
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Booking Date:</Typography>
                <Typography variant="body2">
                  {selectedBooking.bookingInfo.bookingForDate}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Time Slot:</Typography>
                <Typography variant="body2">
                  {new Date(
                    selectedBooking.bookingInfo.startTime
                  ).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  - {" "}
                  {new Date(
                    selectedBooking.bookingInfo.endTime
                  ).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total Hours:</Typography>
                <Typography variant="body2">
                  {selectedBooking.bookingInfo.payment.totalHours}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total Amount:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  ${selectedBooking.bookingInfo.payment.totalAmount.toFixed(2)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Special Requests
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, mt: 1, backgroundColor: "#fafafa" }}
            >
              <Typography variant="body2">
                {selectedBooking.bookingInfo.specialRequests ||
                  "No special requests"}
              </Typography>
            </Paper>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => {
            onClose();
            onEditStatus(selectedBooking);
          }}
        >
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewBookingDialog;